import { getGa4ItemFromCartItem } from "~/common/helpers/get-ga4-item-from-cart-item";
import type {
	CartItem, CartItemMerged
} from "~/common/interfaces/cart-item.interface";
import type { ItemTrimmed } from "~/common/interfaces/item-trimmed";
import type { OrderItem } from "~/common/interfaces/order-item.interface";

export const useCartStore = defineStore(
	"cart",
	() => {
		const cart = ref<CartItem[]>([]);

		const getCartItemsAmount = computed(() => cart.value.reduce((acc, cartItem) => {
			acc += cartItem.amount;

			return acc;
		}, 0)
		);
		const getCartSummary = computed(() => getCartItemsAdapted.value.reduce(
			(acc, next) => {
				acc.subtotal += next.subtotal;
				acc.total += next.total;
				acc.discount -= acc.total - acc.subtotal;

				return acc;
			},
			{
				subtotal: 0,
				total: 0,
				discount: 0
			}
		));
		const getCartItemsForOrder = computed(() => {
			return getCartItemsAdapted.value.reduce(
				(acc, next) => {
					const { ids, product, size, material, amount, text } = next;

					const amountToManufacture = amount - ids.length;
					if (amountToManufacture < 0) {
						ids.length = amount;
					}
					ids.forEach(id => acc.stockItemIds.push(id));

					for (let i = 0; i < amountToManufacture; i++) {
						acc.items.push({
							itemPrototypeId: product.id,
							materialId: material.id,
							sizeId: size.id,
							text
						});
					}

					return acc;
				},
				{
					stockItemIds: [] as number[],
					items: [] as OrderItem[]
				}
			);
		});
		const getCartItemsAdapted = computed(() => cart.value.reduce<CartItemMerged[]>((cart, cartItem) => {
			const ids: number[] = [];
			let subtotal = 0;
			let total = 0;

			const items: (Pick<ItemTrimmed, "price" | "discountedPrice"> & {id: number})[] = [];
			cartItem.product.items.forEach((item) => {
				item.ids.forEach((id) => {
					items.push({
						id,
						discountedPrice: item.discountedPrice,
						price: item.price
					});
				});
			});

			items
				.slice(0, cartItem.amount)
				.forEach((item) => {
					ids.push(item.id);
					subtotal += item.price;
					total += item.discountedPrice || item.price;
				});

			const amountToManufacture = cartItem.amount - items.length;
			if (amountToManufacture > 0) {
				const customTextPrice = cartItem.text ? cartItem.product.feature.customTextPrice * amountToManufacture : 0;
				const price = (cartItem.pricePolicy?.price || 0) * amountToManufacture;
				subtotal += price + customTextPrice;
				total += price + customTextPrice;
			}

			const cartItemMerged: CartItemMerged = {
				...cartItem,
				ids,
				subtotal,
				total
			};

			cart.push(cartItemMerged);

			return cart;
		}, []));

		const addItemToCart = (cartItem: CartItem) => {
			/** @todo add error handler */
			const expectedAmount = cartItem.amount + getCartItemsAmount.value;
			if (expectedAmount > 100) {
				return;
			}

			const foundInCart = cart.value.find(
				({ product, material, size, text }) =>
					product.id === cartItem.product.id &&
					material.id === cartItem.material.id &&
					size.id === cartItem.size.id &&
					text === cartItem.text
			);

			if (foundInCart) {
				foundInCart.amount += cartItem.amount;
			} else {
				const _cartItem: CartItem = JSON.parse(JSON.stringify(cartItem));
				_cartItem.product.items = _cartItem.product.items.filter(item => item.material.id === _cartItem.material.id && item.size.id === _cartItem.size.id && item.text === _cartItem.text);
				cart.value.push(_cartItem);
			}

			const ga4Item = getGa4ItemFromCartItem(cartItem);
			const ga4Obj = {
				currency: "UAH",
				value: (ga4Item.price || 0) * (ga4Item.quantity || 1),
				items: [ga4Item]
			};
			useTrackEvent("add_to_cart", ga4Obj);
		};
		const changeItemAmount = (index: number, amount: number) => {
			const foundInCart = cart.value[index];
			if (!foundInCart) {
				return;
			}

			/** @todo add error handler */
			const increaseAction = amount - foundInCart.amount > 0;
			if (increaseAction && getCartItemsAmount.value >= 100) {
				return;
			}

			foundInCart.amount = amount;

			const ga4Item = getGa4ItemFromCartItem({ ...foundInCart, amount: 1 });
			const ga4Obj = {
				currency: "UAH",
				value: ga4Item.price,
				items: [ga4Item]
			};
			increaseAction ? useTrackEvent("add_to_cart", ga4Obj) : useTrackEvent("remove_from_cart", ga4Obj);
		};
		const deleteProductFromCart = (index: number) => {
			const [cartItem] = cart.value.splice(index, 1);

			const ga4Item = getGa4ItemFromCartItem(cartItem);
			const ga4Obj = {
				currency: "UAH",
				value: (ga4Item.price || 0) * (ga4Item.quantity || 1),
				items: [ga4Item]
			};
			useTrackEvent("remove_from_cart", ga4Obj);
		};
		const purgeCart = () => {
			cart.value = [];
		};

		return {
			cart,

			getCartSummary,
			getCartItemsAdapted,
			getCartItemsForOrder,

			addItemToCart,
			changeItemAmount,
			deleteProductFromCart,
			purgeCart
		};
	},
	{
		persist: {
			paths: ["cart", "checkout"],
			storage: persistedState.localStorage
		}
	}
);
